import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { TrendingUp, Users, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const icons = [Users, Sparkles, TrendingUp];

  return (
    <section 
      id="sobre" 
      data-testid="about-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 relative"
    >
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_4ea5ffca-c37f-4c05-ba81-583edc3692b4/artifacts/bkdtwjfu_nova%20foto.png"
                  alt="Creative content creation workspace with camera and equipment"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#ff00c7]/30 rounded-2xl -z-10" />
            </div>
          </motion.div>

          {/* Right column - Content */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs md:text-sm font-medium uppercase tracking-widest text-[#ff00c7] mb-4"
            >
              {t.about.badge}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="about-title"
              className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#242328] leading-tight mb-6"
            >
              {t.about.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-[#242328]/70 leading-relaxed mb-8"
            >
              {t.about.description}
            </motion.p>

            {/* Features list */}
            <div className="space-y-6">
              {t.about.features.map((feature, index) => {
                const Icon = icons[index];
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    className="flex gap-4 items-start group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#242328] flex items-center justify-center group-hover:bg-[#ff00c7] transition-colors duration-300">
                      <Icon className="w-5 h-5 text-[#f8f6e2]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-['Syne'] text-lg font-semibold text-[#242328] mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-[#242328]/60">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
